import regionData from "../data/world_countries_and_regions.json";

export interface RegionItem {
  id: string;
  type: string;
  common: string;
  names: string[];
  code: string[];
}

export interface RegionOption {
  value: string;
  label: string;
  codes: string[];
}

class RegionMapping {
  private regionMap: Map<string, RegionItem> = new Map();
  private searchableOptions: RegionOption[] = [];

  constructor() {
    this.initializeMapping();
  }

  private initializeMapping(): void {
    // Process the region data
    if (!regionData || !regionData.items) {
      return;
    }

    regionData.items.forEach((item: RegionItem) => {
      this.regionMap.set(item.id, item);

      // Create searchable option
      const option: RegionOption = {
        value: item.id,
        label: item.common,
        codes: item.code,
      };
      this.searchableOptions.push(option);
    });

    // Sort by label for better UX
    this.searchableOptions.sort((a, b) => a.label.localeCompare(b.label));
  }

  /**
   * Get all region/country options for dropdown
   */
  getAllOptions(): RegionOption[] {
    return this.searchableOptions;
  }

  /**
   * Get region/country by ID
   */
  getById(id: string): RegionItem | undefined {
    return this.regionMap.get(id);
  }

  /**
   * Convert selected region/country IDs to country codes for API
   */
  getCountryCodesFromIds(ids: string[]): string[] {
    const allCodes = new Set<string>();

    ids.forEach((id) => {
      const region = this.regionMap.get(id);
      if (region) {
        region.code.forEach((code) => allCodes.add(code));
      }
    });

    return Array.from(allCodes).sort();
  }

  /**
   * Search regions/countries by name - searches both common and names fields
   */
  searchByName(searchTerm: string): RegionOption[] {
    if (!searchTerm) return this.searchableOptions;

    const lowerSearchTerm = searchTerm.toLowerCase();

    return this.searchableOptions.filter((option) => {
      const region = this.regionMap.get(option.value);
      if (!region) return false;

      // Search in common name
      if (region.common.toLowerCase().includes(lowerSearchTerm)) {
        return true;
      }

      // Search in all alternative names
      return region.names.some((name) =>
        name.toLowerCase().includes(lowerSearchTerm)
      );
    });
  }

  /**
   * Convert country codes back to region IDs (for URL params)
   */
  getRegionIdsFromCountryCodes(countryCodes: string[]): string[] {
    const regionIds = new Set<string>();

    countryCodes.forEach((code) => {
      // Find regions that contain this country code
      this.searchableOptions.forEach((option) => {
        const region = this.regionMap.get(option.value);
        if (region && region.code.includes(code)) {
          regionIds.add(region.id);
        }
      });
    });

    return Array.from(regionIds);
  }
}

// Export singleton instance
export const regionMapping = new RegionMapping();
